// Versiyon: 1.0.0 - İrsaliye Görüntüleme Modalı (OfferDisplayModal.tsx Tasarımı)
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

// İrsaliye Kalemi arayüzü
interface WaybillItem {
  id: string;
  stockId?: string;
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  netTotal: number;
  grossTotal: number;
}

// Şirket/Şube bilgileri arayüzü
interface BranchInfoData {
  name: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
  bankAccounts?: Array<{bankName: string; iban: string;}>;
}

// İrsaliye arayüzü
interface WaybillData {
  id: string;
  waybillNumber: string;
  accountId: string;
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  waybillDate: string;
  subtotal: number;
  vatTotal: number;
  grandTotal: number;
  currency: string;
  showPrices: boolean;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
  items?: WaybillItem[];
  branchInfo?: BranchInfoData;
}

interface WaybillDisplayModalProps {
  open: boolean;
  onClose: () => void;
  waybill: WaybillData | null;
  companyId: string | null;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '6px 8px',
  fontSize: '0.8rem',
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[200],
}));

const formatCurrency = (amount: number | undefined, currencyCode: string = 'TRY', localeCode: string = 'tr-TR'): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat(localeCode, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString: string | undefined, localeCode: string = 'tr-TR'): string => {
  if (!dateString) return '-';
  try {
    // ISO formatında bir tarih mi kontrol et (T ve Z içeriyor mu?)
    if (dateString.includes('T') && dateString.includes('Z')) {
      const [tarihKismi] = dateString.split('T');
      const [yil, ay, gun] = tarihKismi.split('-');
      return `${gun}.${ay}.${yil}`;
    }
    
    // Sadece tarih formatındaysa (2025-01-01)
    if (dateString.includes('-') && dateString.length === 10) {
      const [yil, ay, gun] = dateString.split('-');
      return `${gun}.${ay}.${yil}`;
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    
    return new Intl.DateTimeFormat(localeCode, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(localDate);
  } catch (e) {
    console.error("Tarih formatı hatası:", e);
    return dateString;
  }
};

const formatDateTime = (dateTimeString: string | undefined, localeCode: string = 'tr-TR'): string => {
  if (!dateTimeString) return '-';
  try {
    if (dateTimeString.includes('T') && dateTimeString.includes('Z')) {
      const [_, saatKismi] = dateTimeString.split('T');
      const formatliSaat = saatKismi.split('.')[0];
      return formatliSaat;
    }
    
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    return date.toLocaleTimeString(localeCode, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.error("Saat formatı hatası:", e);
    return '-';
  }
};

// Yazdırma stilleri
const printStyles = `
  @media print {
    body > *:not(.MuiDialog-root),
    .MuiDialog-root > *:not(.MuiDialog-container),
    .MuiDialog-container > *:not(.MuiPaper-root),
    .MuiDialog-paper > *:not(.MuiDialogContent-root),
    .MuiDialogTitle-root,
    .MuiDialogActions-root {
      display: none !important;
    }

    .MuiDialog-root,
    .MuiDialog-container,
    .MuiDialog-paper,
    .MuiDialogContent-root {
      display: block !important;
      width: auto !important;
      height: auto !important;
      overflow: visible !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    #printableWaybillContent {
      width: 210mm !important; 
      min-height: 290mm !important;
      padding: 10mm !important;
      margin: 0 auto !important;
      box-shadow: none !important;
      border: none !important;
      background-color: white !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      font-size: 10pt;
    }

    @page {
      size: A4;
      margin: 0;
    }

    #printableWaybillContent .MuiPaper-root {
      box-shadow: none !important;
      border: 1px solid #ccc !important;
      background-color: white !important;
    }

    #printableWaybillContent table {
      width: 100% !important;
      page-break-inside: auto;
    }
    #printableWaybillContent tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    #printableWaybillContent thead {
      display: table-header-group;
    }
    
    /* Fiyat gizleme için print stili */
    .hide-on-print {
      display: none !important;
    }
  }
`;

const WaybillDisplayModal: React.FC<WaybillDisplayModalProps> = ({
  open,
  onClose,
  waybill,
  companyId,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [fetchedBranchInfo, setFetchedBranchInfo] = useState<BranchInfoData | null>(null);
  const [loadingBranchInfo, setLoadingBranchInfo] = useState<boolean>(false);
  const [branchInfoError, setBranchInfoError] = useState<string | null>(null);
  
  // Firma bilgilerini yükleme
  useEffect(() => {
    if (open && companyId) {
      setLoadingBranchInfo(true);
      setBranchInfoError(null);
      setFetchedBranchInfo(null);
      
      getCompanyInfo(companyId)
        .then((info) => {
          if (info) {
            setFetchedBranchInfo(info as BranchInfoData);
          } else {
            setBranchInfoError(tt('Firma bilgileri bulunamadı.', 'Company information not found.'));
            setFetchedBranchInfo({ name: tt('Firma Bilgileri Yok', 'No Company Information') });
          }
        })
        .catch((err) => {
          console.error("Firma bilgileri yüklenirken hata:", err);
          setBranchInfoError(tt('Firma bilgileri yüklenirken bir hata oluştu.', 'An error occurred while loading company information.'));
          setFetchedBranchInfo({ name: tt('Hata oluştu', 'An error occurred') });
        })
        .finally(() => {
          setLoadingBranchInfo(false);
        });
    } else {
      setFetchedBranchInfo(null);
      setLoadingBranchInfo(false);
      setBranchInfoError(null);
    }
  }, [open, companyId]);
  
  // İrsaliye yoksa hiçbir şey gösterme
  if (!waybill) {
    return null;
  }

  console.log("İrsaliye Verisi:", waybill);
  console.log("CompanyId:", companyId);

  // İrsaliye kalemlerini hazırla
  const items = waybill.items || [];
  console.log("İrsaliye Kalemleri:", items);
  
  // Cari bilgilerini hazırla
  const accountInfo = waybill.account;
  console.log("Cari Bilgileri:", accountInfo);

  // Fiyat göster/gizle durumu
  const showPrices = waybill.showPrices;

  // PDF olarak indirme fonksiyonu
  const handleDownloadPdf = () => {
    const printableContent = document.getElementById('printableWaybillContent');
    if (!printableContent) return;

    html2canvas(printableContent, { 
      scale: 2, 
      logging: false,
      useCORS: true,
      allowTaint: true
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${tt('Waybill', 'Waybill')}_${waybill.waybillNumber}.pdf`);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <GlobalStyles styles={printStyles} />
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', p: 1.5, px: 2.5 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {tt('SATIŞ İRSALİYESİ', 'SALES WAYBILL')}
          </Typography>
          {!showPrices && (
            <Chip 
              icon={<VisibilityOffIcon sx={{ color: 'white !important' }} />}
              label={tt('Fiyatlar Gizli', 'Prices Hidden')}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          )}
        </Grid>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: '#f9f9f9' }}>
        <Box id="printableWaybillContent" sx={{ p: '20px', m:'auto', width: '210mm', minHeight: '297mm', backgroundColor: 'white', boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
          {/* İrsaliye Başlığı ve Numarası */}
          <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
            <Grid item xs={7}>
              {loadingBranchInfo ? (
                <CircularProgress size={20} />
              ) : fetchedBranchInfo ? (
                <>
                  <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                    {fetchedBranchInfo.name}
                  </Typography>
                  {fetchedBranchInfo.address && (
                    <Typography variant="body2" display="block">
                      {fetchedBranchInfo.address}
                    </Typography>
                  )}
                  {(fetchedBranchInfo.taxOffice || fetchedBranchInfo.taxNumber) && (
                    <Typography variant="body2" display="block">
                      <strong>{tt('Vergi Dairesi:', 'Tax Office:')}</strong> {fetchedBranchInfo.taxOffice || '-'} / <strong>{tt('V.K.N:', 'Tax No:')}</strong> {fetchedBranchInfo.taxNumber || '-'}
                    </Typography>
                  )}
                  {(fetchedBranchInfo.phone || fetchedBranchInfo.email) && (
                    <Typography variant="body2" display="block">
                      {tt('Tel:', 'Phone:')} {fetchedBranchInfo.phone || '-'} {fetchedBranchInfo.email && `| ${tt('E-posta', 'Email')}: ${fetchedBranchInfo.email}`}
                    </Typography>
                  )}
                  {(fetchedBranchInfo.mersisNo || fetchedBranchInfo.tradeRegistryNo) && (
                    <Typography variant="body2" display="block">
                      {tt('Mersis No', 'Mersis No')}: {fetchedBranchInfo.mersisNo || '-'} | {tt('Tic. Sic. No', 'Trade Reg. No')}: {fetchedBranchInfo.tradeRegistryNo || '-'}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="error">{branchInfoError || tt('Firma bilgileri yüklenemedi.', 'Company information could not be loaded.')}</Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'right', border: '1px solid #eee', p: 1, borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'black' }}>{tt('İRSALİYE', 'WAYBILL')}</Typography>
              <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'bold', color: 'error.main', fontSize: '1.1rem' }}>
                {waybill.waybillNumber}
              </Typography>
              <Divider sx={{ my: 0.5 }}/>
              <Typography variant="body2"><strong>{tt('İrsaliye Tarihi:', 'Waybill Date:')}</strong> {formatDate(waybill.waybillDate, localeCode)}</Typography>
              <Typography variant="body2"><strong>{tt('Düzenleme Saati:', 'Issue Time:')}</strong> {formatDateTime(waybill.createdAt, localeCode)}</Typography>
              {!showPrices && (
                <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
                  <VisibilityOffIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  {tt('Fiyatlar Gizli', 'Prices Hidden')}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Cari Bilgileri */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#fafafa' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {tt('MÜŞTERİ BİLGİLERİ', 'CUSTOMER INFORMATION')}
            </Typography>
            
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{accountInfo.companyName}</Typography>
            {accountInfo.taxNumber && <Typography variant="body2"><strong>{tt('V.K.N / T.C. Kimlik No:', 'Tax No / National ID:')}</strong> {accountInfo.taxNumber}</Typography>}
            {accountInfo.taxOffice && <Typography variant="body2"><strong>{tt('Vergi Dairesi:', 'Tax Office:')}</strong> {accountInfo.taxOffice}</Typography>}
            {accountInfo.address && <Typography variant="body2"><strong>{tt('Adres:', 'Address:')}</strong> {accountInfo.address}</Typography>}
            {accountInfo.phone && <Typography variant="body2"><strong>{tt('Telefon:', 'Phone:')}</strong> {accountInfo.phone}</Typography>}
            {accountInfo.email && <Typography variant="body2"><strong>{tt('E-posta:', 'Email:')}</strong> {accountInfo.email}</Typography>}
          </Paper>

          {/* Ürün/Hizmet Tablosu */}
          {items && items.length > 0 ? (
            <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small" aria-label="irsaliye kalemleri tablosu">
                <TableHead sx={{ backgroundColor: '#f0f0f0' }}>
                  <TableRow>
                    <StyledTableHeadCell sx={{ width: '30px' }}>#</StyledTableHeadCell>
                      <StyledTableHeadCell>{tt('Mal / Hizmet', 'Product / Service')}</StyledTableHeadCell>
                    <StyledTableHeadCell align="right" sx={{ width: '70px' }}>{tt('Miktar', 'Quantity')}</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ width: '60px' }}>{tt('Birim', 'Unit')}</StyledTableHeadCell>
                    {showPrices && (
                      <>
                        <StyledTableHeadCell align="right" sx={{ width: '100px' }}>{tt('Birim Fiyat', 'Unit Price')}</StyledTableHeadCell>
                        <StyledTableHeadCell align="right" sx={{ width: '60px' }}>KDV (%)</StyledTableHeadCell>
                        <StyledTableHeadCell align="right" sx={{ width: '110px' }}>{tt('Tutar', 'Amount')} ({waybill.currency})</StyledTableHeadCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <StyledTableCell component="th" scope="row">{index + 1}</StyledTableCell>
                      <StyledTableCell sx={{ fontWeight: '500' }}>{item.itemName}</StyledTableCell>
                      <StyledTableCell align="right">{item.quantity.toLocaleString(localeCode)}</StyledTableCell>
                      <StyledTableCell>{item.unit}</StyledTableCell>
                      {showPrices && (
                        <>
                          <StyledTableCell align="right">{formatCurrency(item.unitPrice, waybill.currency, localeCode).replace(waybill.currency, '').trim()}</StyledTableCell>
                          <StyledTableCell align="right">{item.vatRate}%</StyledTableCell>
                          <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(item.grossTotal, waybill.currency, localeCode).replace(waybill.currency, '').trim()}
                          </StyledTableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">{tt('Bu irsaliyede ürün/hizmet kaydı bulunmamaktadır.', 'No product/service record found in this waybill.')}</Typography>
            </Paper>
          )}

          {/* Toplamlar Bölümü - Sadece fiyat gösteriliyorsa */}
          {showPrices && (
            <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={7} md={6} lg={5}>
                <TableContainer component={Paper} variant='outlined'>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <StyledTableCell>{tt('ARA TOPLAM', 'SUBTOTAL')}</StyledTableCell>
                        <StyledTableCell align="right">{formatCurrency(waybill.subtotal, waybill.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                      <TableRow>
                        <StyledTableCell>{tt('TOPLAM KDV', 'TOTAL VAT')}</StyledTableCell>
                        <StyledTableCell align="right">{formatCurrency(waybill.vatTotal, waybill.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                        <StyledTableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{tt('GENEL TOPLAM', 'GRAND TOTAL')}</StyledTableCell>
                        <StyledTableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{formatCurrency(waybill.grandTotal, waybill.currency, localeCode)}</StyledTableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}

          {/* Notlar */}
          {waybill.notes && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 2, backgroundColor: '#fafafa' }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>{tt('İrsaliye Notları:', 'Waybill Notes:')}</Typography>
              <Typography variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                {waybill.notes}
              </Typography>
            </Paper>
          )}

          <Divider sx={{ my: 2 }} />
          
          {/* İmza Alanları */}
          <Grid container spacing={2} sx={{ mt: 4 }}>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 80, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{tt('TESLİM EDEN', 'DELIVERED BY')}</Typography>
                <Box sx={{ mt: 4 }}>
                  <Typography variant="caption">{tt('İmza / Kaşe', 'Signature / Stamp')}</Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 80, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{tt('TESLİM ALAN', 'RECEIVED BY')}</Typography>
                <Box sx={{ mt: 4 }}>
                  <Typography variant="caption">{tt('İmza / Kaşe', 'Signature / Stamp')}</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          <Typography variant="caption" display="block" sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.7rem', mt: 2 }}>
            {tt('Bu irsaliye elektronik ortamda düzenlenmiştir.', 'This waybill is issued electronically.')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 1.5, backgroundColor: 'grey.100', borderTop: '1px solid #ddd' }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          {tt('Kapat', 'Close')}
        </Button>
        <Button 
          onClick={handleDownloadPdf} 
          variant="outlined" 
          color="primary"
          sx={{ mr: 1 }}
        >
          {tt('PDF İndir', 'Download PDF')}
        </Button>
        <Button 
          onClick={() => window.print()} 
          variant="contained" 
          color="secondary"
        >
          {tt('Yazdır', 'Print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaybillDisplayModal;
export type { WaybillData };
